import { supabaseClient } from "@/lib/supabase";
import type { DiaryAccess } from "@/types/database/diaries";
import { useMutation } from "@tanstack/react-query";
import { toast } from "sonner";
import { useDiaryInvalidation } from "../../diaries/api/diaryInvalidation";
import useInvalidateDiaries from "@/features/diaries/features/edit/api/invalidateDiaries";

export async function insertDiaryAccess(diaryAccess: DiaryAccess["Insert"]) {
	const { data, error } = await supabaseClient
		.from("diary_access")
		.insert(diaryAccess)
		.select("*")
		.single();
	if (error) throw error;
	return data;
}

const diary_access_mutation_key = ["diary_access"];

export const useInsertDiaryAccess = (diaryId: string) => {
	const { invalidateById } = useDiaryInvalidation();
	return useMutation({
		mutationKey: diary_access_mutation_key,
		mutationFn: (diaryAccess: DiaryAccess["Insert"]) =>
			insertDiaryAccess(diaryAccess),
		onError: () => {
			toast.error("Failed to save diary access");
		},
		onSuccess: () => {
			toast.success("Diary access saved");
		},
		onSettled: () => {
			invalidateById(diaryId);
		},
	});
};

export async function deleteDiaryAccess({ diary_id, user_id }: { diary_id: string; user_id: string }) {
	const { error } = await supabaseClient
		.from("diary_access")
		.delete()
		.eq("diary_id", diary_id)
		.eq("user_id", user_id);
	if (error) throw error;
	return { diary_id, user_id };
}

export function useDeleteDiaryAccess() {
	const invalidateDiaries = useInvalidateDiaries();
	return useMutation({
		mutationFn: deleteDiaryAccess,
		onError: () => {
			toast.error("Failed to remove diary access");
		},
		onSuccess: () => {
			toast.success("Diary access removed");
		},
		onSettled: (_data, _error, variables) => {
			if (variables?.diary_id) invalidateDiaries(variables.diary_id);
		},
	});
}
